import { type JSX, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '~/hooks/useAuth';
import type { ApiError } from '~/services/api/client';
import { getMyOrganizations } from '~/services/api/organizations';
import { clearActiveOrgId, getActiveOrgId, setActiveOrgId } from '~/utils/activeOrg';
import { logger } from '~/utils/logger';

import type { OrgRole } from '~/types';

import { TenantContext } from './TenantContext.context';

interface TenantProviderProps {
  children: ReactNode;
}

const ORGANIZATION_QUERY_KEY = ['organization', 'current'] as const;

/** Routes where org check query should be disabled and redirect skipped */
const SKIP_ORG_CHECK_ROUTES = ['/onboarding', '/invitations'];

const shouldSkipOrgCheck = (pathname: string): boolean =>
  SKIP_ORG_CHECK_ROUTES.some(route => pathname.startsWith(route));

export function TenantProvider({ children }: TenantProviderProps): JSX.Element {
  const { loading: authLoading, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Active organization ID state with localStorage persistence
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() => getActiveOrgId());

  // Refs for tracking user changes (follow AuthContext pattern lines 38-39)
  const previousUserRef = useRef<typeof user>(null);
  const isInitialMountRef = useRef(true);

  // After Firebase auth completes, fetch user's organization
  // Use query key ['organization', 'current']
  const {
    data: organizations,
    error,
    isFetching: isQueryFetching,
    isLoading: isQueryLoading,
    isSuccess,
  } = useQuery({
    queryKey: ORGANIZATION_QUERY_KEY,
    queryFn: getMyOrganizations,
    enabled: !!user && !shouldSkipOrgCheck(location.pathname),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) - these are expected conditions (no org = redirect)
      // Only retry on network errors or server errors (5xx)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });

  // Invalidate query cache on user change (follow AuthContext pattern lines 54-63)
  useEffect(() => {
    const previousUser = previousUserRef.current;
    const isInitialMount = isInitialMountRef.current;
    isInitialMountRef.current = false;

    const userChanged = previousUser?.uid !== user?.uid;
    previousUserRef.current = user;

    // If user logged in (was null, now has user) and it's not initial mount, invalidate
    if (!isInitialMount && !previousUser && user) {
      logger.info(
        { component: 'TenantContext' },
        'User logged in, invalidating organization queries',
      );
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
    } else if (!isInitialMount && userChanged && user) {
      // User switched (different user ID)
      logger.info(
        { component: 'TenantContext' },
        'User changed, invalidating organization queries',
      );
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
    }
  }, [user, queryClient]);

  // Redirect to /onboarding if user has no organization
  useEffect(() => {
    // Check for "no_organization" API error (403 response from backend)
    // Use duck-typing since error may not be instanceof ApiError after query wrapping
    const apiError = error as ApiError | null;
    const isNoOrgError =
      apiError?.status === 403 &&
      (apiError?.details as { error?: string })?.error === 'no_organization';

    // Skip redirect on routes that handle their own org logic
    if (shouldSkipOrgCheck(location.pathname)) return;

    // Redirect if: authenticated + (no-org error OR empty orgs array).
    // Guard on !isQueryFetching: never redirect while the query is in flight —
    // a stale cached error during a re-fetch would otherwise cause a /onboarding loop.
    if (
      !authLoading &&
      !isQueryFetching &&
      user &&
      (isNoOrgError || (isSuccess && organizations?.length === 0))
    ) {
      logger.info(
        { component: 'TenantContext' },
        'No organizations found, redirecting to onboarding',
      );
      void navigate('/onboarding', { replace: true });
    }
  }, [
    authLoading,
    isQueryFetching,
    user,
    isSuccess,
    organizations,
    error,
    navigate,
    location.pathname,
  ]);

  const refreshOrganization = useCallback(async (): Promise<void> => {
    logger.info({ component: 'TenantContext' }, 'Refreshing organization data');
    await queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY });
  }, [queryClient]);

  // Set active organization: persist the new org, then hard-reload to /dashboard so every
  // query re-fetches under the new org context. Cache keys (['project', id], ['projects'],
  // etc.) carry no org dimension, so invalidateQueries() alone can briefly serve the previous
  // org's cached data — a full reload sidesteps that without re-keying every query.
  const setActiveOrg = useCallback((orgId: string) => {
    setActiveOrgId(orgId);
    window.location.assign('/dashboard');
  }, []);

  // Determine current org: use activeOrgId if set and valid, otherwise first org.
  // Pure derivation only — side effects (localStorage sync, state updates) live in useEffects below.
  const currentOrg = useMemo(() => {
    if (!organizations?.length) return null;

    if (activeOrgId) {
      const found = organizations.find(org => org.id === activeOrgId);
      if (found) return found;
      // activeOrgId is stale (user lost access) — fall through to default
    }

    return organizations[0];
  }, [organizations, activeOrgId]);

  // Clear stale activeOrgId when the user no longer has access to the saved org
  useEffect(() => {
    if (!activeOrgId || !organizations?.length) return;
    const found = organizations.find(org => org.id === activeOrgId);
    if (!found) {
      logger.warn(
        { component: 'TenantContext' },
        'Active org ID not found in user organizations, using default',
        { activeOrgId, availableOrgs: organizations.map(o => o.id) },
      );
      // Both calls are required: setActiveOrgIdState clears the React state that currentOrg's
      // useMemo derives from (this effect does not reload, unlike setActiveOrg), while
      // clearActiveOrgId clears the persisted copy so client.ts stops sending the stale ID.
      setActiveOrgIdState(null);
      clearActiveOrgId();
    }
  }, [activeOrgId, organizations]);

  // Sync localStorage with the resolved current org so client.ts always has the correct
  // X-Organization-ID header value (handles logout clearing localStorage while state lags).
  useEffect(() => {
    if (!currentOrg) return;
    const stored = getActiveOrgId();
    if (stored !== currentOrg.id) {
      setActiveOrgId(currentOrg.id);
    }
  }, [currentOrg]);

  const currentOrgRole: OrgRole | null = currentOrg?.role ?? null;

  // Tenant is ready when we have a currentOrg
  // No need to check activeOrgId sync - we handle that in currentOrg useMemo
  const isTenantReady = !!currentOrg;

  // Loading state - true while auth or query is loading
  const loading = authLoading || (!!user && isQueryLoading);

  // Context value with useMemo for performance
  const value = useMemo(
    () => ({
      currentOrg,
      currentOrgRole,
      isTenantReady,
      loading,
      refreshOrganization,
      organizations: organizations ?? [],
      setActiveOrg,
    }),
    [
      currentOrg,
      currentOrgRole,
      isTenantReady,
      loading,
      refreshOrganization,
      organizations,
      setActiveOrg,
    ],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
